import { useState } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Filter } from "lucide-react";

const Shop = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const { data: allProducts, isLoading: productsLoading } = useProducts();
  const { data: categories, isLoading: categoriesLoading } = useCategories();

  const filteredProducts = selectedCategory
    ? allProducts?.filter((product) =>
        product.product_categories?.some(
          (pc: any) => pc.category?.wordpress_id === selectedCategory
        )
      )
    : allProducts;

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-grow pt-20">
        <section className="bg-gradient-to-br from-primary/10 via-accent/5 to-background py-12 sm:py-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-gold/10 rounded-full blur-3xl animate-float" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-center text-primary mb-3 sm:mb-4 animate-fade-in px-2">
              Magazin Online
            </h1>
            <p className="text-center text-muted-foreground text-base sm:text-lg max-w-3xl mx-auto font-lora leading-relaxed animate-fade-in px-4" style={{ animationDelay: '0.2s' }}>
              Descoperă întreaga noastră colecție de cărți religioase legate în piele,
              create cu pasiune și dedicație
            </p>
          </div>
        </section>

        <section className="py-6 sm:py-8 bg-cream/20 border-y border-border/50">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              <span className="font-semibold text-foreground text-sm sm:text-base">Filtrează după categorie:</span>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <Button
                variant={selectedCategory === null ? "default" : "outline"}
                onClick={() => setSelectedCategory(null)}
                className="hover-scale text-xs sm:text-sm"
                size="sm"
              >
                Toate produsele
              </Button>
              {categoriesLoading ? (
                [...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-8 sm:h-10 w-24 sm:w-32" />
                ))
              ) : (
                categories?.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.wordpress_id ? "default" : "outline"}
                    onClick={() => setSelectedCategory(category.wordpress_id)}
                    className="hover-scale text-xs sm:text-sm"
                    size="sm"
                  >
                    {category.name}
                  </Button>
                ))
              )}
            </div>
          </div>
        </section>

        <section className="py-12 sm:py-16 bg-gradient-to-b from-background to-cream/30">
          <div className="container mx-auto px-4 sm:px-6">
            {productsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {[...Array(8)].map((_, i) => (
                  <Card key={i} className="overflow-hidden animate-fade-in" style={{ animationDelay: `${i * 50}ms` }}>
                    <Skeleton className="h-56 sm:h-64 w-full" />
                    <CardContent className="p-4 sm:p-6">
                      <Skeleton className="h-5 sm:h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-6 sm:h-8 w-20 sm:w-24 mb-3 sm:mb-4" />
                      <Skeleton className="h-9 sm:h-10 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredProducts && filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {filteredProducts.map((product, index) => (
                  <Card
                    key={product.id}
                    className="group overflow-hidden hover:shadow-2xl transition-all duration-500 hover-lift cursor-pointer border-2 border-transparent hover:border-gold/30 glass-effect animate-fade-in relative"
                    onClick={() => navigate(`/produs/${product.slug}`)}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-gold/0 via-gold/5 to-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    <div className="relative h-56 sm:h-64 overflow-hidden bg-cream/50">
                      {product.product_images && product.product_images.length > 0 ? (
                        <img
                          src={product.product_images[0].src}
                          alt={product.product_images[0].alt || product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted">
                          <span className="text-muted-foreground text-sm">Fără imagine</span>
                        </div>
                      )}
                      {product.sale_price && product.sale_price < (product.regular_price || product.price) && (
                        <Badge className="absolute top-3 left-3 bg-red-500 hover:bg-red-600">
                          Reducere
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4 sm:p-6 bg-gradient-to-b from-card to-cream/30 relative">
                      <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-all font-playfair">
                        {product.name}
                      </h3>
                      {product.short_description && (
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2 font-lora">
                          {product.short_description.replace(/<[^>]*>/g, "")}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mb-4">
                        {product.sale_price && product.sale_price < product.regular_price! ? (
                          <>
                            <p className="text-2xl font-bold text-primary">
                              {product.sale_price.toFixed(2)} RON
                            </p>
                            <p className="text-lg text-muted-foreground line-through">
                              {product.regular_price?.toFixed(2)} RON
                            </p>
                          </>
                        ) : (
                          <p className="text-2xl font-bold text-primary">
                            {product.price.toFixed(2)} RON
                          </p>
                        )}
                      </div>
                      <Button className="w-full shadow-lg hover-glow-gold group/btn relative overflow-hidden">
                        <span className="relative z-10">Vezi detalii</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg mb-2">
                  Nu există produse în această categorie
                </p>
                <Button variant="outline" onClick={() => setSelectedCategory(null)} className="hover-scale">
                  Vezi toate produsele
                </Button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Shop;
